import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HiColorSwatch,
  HiDocumentAdd,
  HiPencil,
  HiPlus,
  HiSearch,
  HiTrash,
  HiUpload,
  HiX,
} from 'react-icons/hi';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { apiConfig } from '../config/environment';
import { normalizeApiData, normalizeImageUrl } from '../utils/apiHelpers';
import './Clients.css';
import './Catalogo.css';

const CATEGORIES = [
  { id: 'calcinha', label: 'Calcinha' },
  { id: 'chinelo', label: 'Chinelo' },
];

const SIZE_PRESETS = {
  calcinha: ['PP', 'P', 'M', 'G', 'GG'],
  chinelo: ['33/34', '35/36', '37/38', '39/40', '41/42', '43/44'],
};

const pairChineloSizes = (sizes = []) => {
  const pairs = new Map();
  const other = [];
  sizes.forEach((raw) => {
    const token = String(raw || '').trim();
    if (!token) return;
    const slash = token.match(/^(\d{2})\s*[/\-–]\s*(\d{2})$/);
    const single = token.match(/^(\d{2})$/);
    const n = slash ? Number(slash[1]) : single ? Number(single[1]) : NaN;
    if (!Number.isFinite(n)) {
      other.push(token);
      return;
    }
    const odd = n % 2 === 0 ? n - 1 : n;
    pairs.set(odd, `${odd}/${odd + 1}`);
  });
  return [...[...pairs.entries()].sort((a, b) => a[0] - b[0]).map(([, label]) => label), ...other];
};

const sizesForProduct = (item) => {
  const sizes = Array.isArray(item?.sizes) ? item.sizes : [];
  return item?.category === 'chinelo' ? pairChineloSizes(sizes) : sizes;
};

const COLOR_PRESETS = ['Preto', 'Branco', 'Bege', 'Rosa', 'Azul', 'Vermelho', 'Nude'];

const STATUS_LABELS = {
  pending: 'Pendente',
  contacted: 'Em contato',
  fulfilled: 'Concluído',
  cancelled: 'Cancelado',
};

const emptyProduct = {
  category: 'calcinha',
  model: '',
  name: '',
  description: '',
  price: '',
  sizes: ['P', 'M', 'G'],
  colors: ['Preto', 'Branco'],
  active: true,
  sort_order: 0,
};

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const categoryLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id || '—';

const catalogImageUrl = (raw) => normalizeImageUrl(raw, apiConfig.assetBaseURL);

const uniqueOrderProducts = (order, resolveImage, max = 5) => {
  const seen = new Set();
  const thumbs = [];
  (order?.items || []).forEach((item) => {
    const key = String(item.product_id || item.product_name || item.id);
    if (seen.has(key)) return;
    seen.add(key);
    thumbs.push({ ...item, preview: resolveImage(item) });
  });
  return { thumbs: thumbs.slice(0, max), extra: Math.max(0, thumbs.length - max) };
};

const itemMeta = (item) => [item.size, item.color].filter(Boolean).join(' · ');

const OrderItemPhoto = ({ src, alt = '' }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <span className="catalog-thumb-fallback" aria-hidden="true" />;
  }
  return <img src={src} alt={alt} onError={() => setFailed(true)} />;
};

const TagField = ({ label, values, onChange, placeholder, suggestions = [] }) => {
  const [draft, setDraft] = useState('');

  const add = (raw) => {
    const tag = String(raw || '').trim();
    if (!tag) return;
    if (values.some((v) => v.toLowerCase() === tag.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...values, tag]);
    setDraft('');
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="catalog-tags">
        {values.map((tag) => (
          <span key={tag} className="catalog-tag">
            {tag}
            <button type="button" onClick={() => onChange(values.filter((v) => v !== tag))} aria-label={`Remover ${tag}`}>
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(draft);
            }
            if (e.key === 'Backspace' && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => add(draft)}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="catalog-tag-presets">
          {suggestions
            .filter((s) => !values.includes(s))
            .map((s) => (
              <button key={s} type="button" className="catalog-preset" onClick={() => add(s)}>
                + {s}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

const Catalogo = () => {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyProduct });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingPdf, setPendingPdf] = useState(null);
  const [importingPdf, setImportingPdf] = useState(false);
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const [deactivatePrevious, setDeactivatePrevious] = useState(true);
  const [activeFilter, setActiveFilter] = useState('true');
  const [openOrder, setOpenOrder] = useState(null);
  const [editingStatus, setEditingStatus] = useState(false);
  const [draftStatus, setDraftStatus] = useState('pending');
  const [savingStatus, setSavingStatus] = useState(false);
  const [productImageById, setProductImageById] = useState({});
  const pdfInputRef = useRef(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCatalogProducts({
        search: searchTerm || undefined,
        category: categoryFilter || undefined,
        active: activeFilter === '' ? undefined : activeFilter,
      });
      setProducts(normalizeApiData(data));
    } catch (error) {
      setProducts([]);
      showWarning(error?.message || 'Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCatalogOrders({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        per_page: 20,
      });
      setOrders(normalizeApiData(data));
      setTotal(data?.total ?? 0);
      setLastPage(data?.last_page ?? 1);
      setPendingCount(data?.pending_count ?? 0);
    } catch (error) {
      setOrders([]);
      showWarning(error?.message || 'Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'products') loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, searchTerm, categoryFilter, activeFilter]);

  useEffect(() => {
    if (tab === 'orders') loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, searchTerm, statusFilter, currentPage]);

  useRealtimeRefresh(['catalog'], () => {
    if (tab === 'products') loadProducts();
    if (tab === 'orders') loadOrders();
  });

  useEffect(() => {
    if (tab !== 'orders') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getCatalogProducts();
        if (cancelled) return;
        const map = {};
        normalizeApiData(data).forEach((product) => {
          if (product?.id && product.image_url) {
            map[product.id] = product.image_url;
          }
        });
        setProductImageById(map);
      } catch {
        if (!cancelled) setProductImageById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const resolveItemImage = (item) =>
    catalogImageUrl(item?.image_url || productImageById[item?.product_id] || '');

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyProduct });
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      category: item.category || 'calcinha',
      model: item.model || '',
      name: item.name || '',
      description: item.description || '',
      price: item.price != null ? String(item.price) : '',
      sizes: sizesForProduct(item),
      colors: Array.isArray(item.colors) ? item.colors : [],
      active: item.active !== false,
      sort_order: item.sort_order ?? 0,
    });
    setImageFile(null);
    setImagePreview(item.image_url || '');
    setShowModal(true);
  };

  const onCategoryChange = (category) => {
    setForm((prev) => ({
      ...prev,
      category,
      sizes: category === 'chinelo'
        ? (prev.sizes.length ? pairChineloSizes(prev.sizes) : SIZE_PRESETS.chinelo)
        : (prev.sizes.length ? prev.sizes : SIZE_PRESETS[category] || []),
    }));
  };

  const handleImage = (file) => {
    setImageFile(file || null);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.price === '') {
      showWarning('Informe nome e valor do produto.');
      return;
    }
    if (form.category === 'chinelo' && !form.model.trim()) {
      showWarning('Informe o modelo do chinelo para agrupar no catálogo.');
      return;
    }
    if (!editingId && !imageFile) {
      showWarning('Envie uma imagem do produto.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, image: imageFile };
      if (editingId) {
        await apiService.updateCatalogProduct(editingId, payload);
        showSuccess('Produto atualizado.');
      } else {
        await apiService.createCatalogProduct(payload);
        showSuccess('Produto lançado no catálogo.');
      }
      setShowModal(false);
      loadProducts();
    } catch (error) {
      showWarning(error?.message || 'Não foi possível salvar o produto');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!productToDelete?.id) return;
    try {
      const res = await apiService.deleteCatalogProduct(productToDelete.id);
      showSuccess(res?.message || 'Produto removido.');
      setProductToDelete(null);
      loadProducts();
    } catch (error) {
      showWarning(error?.message || 'Falha ao excluir');
    }
  };

  const openImportModal = () => {
    setPendingPdf(null);
    setPdfDragOver(false);
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    if (importingPdf) return;
    setShowImportModal(false);
    setPendingPdf(null);
    setPdfDragOver(false);
  };

  const acceptPdfFile = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
      showWarning('Envie um arquivo PDF.');
      return;
    }
    setPendingPdf(file);
  };

  const onPdfPicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    acceptPdfFile(file);
  };

  const onPdfDrop = (e) => {
    e.preventDefault();
    setPdfDragOver(false);
    acceptPdfFile(e.dataTransfer?.files?.[0]);
  };

  const confirmImportPdf = async () => {
    if (!pendingPdf || importingPdf) return;
    setImportingPdf(true);
    try {
      const res = await apiService.importCatalogPdf(pendingPdf, 'chinelo', deactivatePrevious);
      showSuccess(res?.message || 'Chinelos importados do PDF.');
      setShowImportModal(false);
      setPendingPdf(null);
      setDeactivatePrevious(false);
      setActiveFilter('true');
      setTab('products');
      loadProducts();
    } catch (error) {
      showWarning(error?.message || 'Não foi possível importar o PDF');
    } finally {
      setImportingPdf(false);
    }
  };

  const openOrderModal = (order) => {
    setOpenOrder(order);
    setEditingStatus(false);
    setDraftStatus(order.status || 'pending');
  };

  const closeOrderModal = () => {
    setOpenOrder(null);
    setEditingStatus(false);
    setSavingStatus(false);
  };

  const saveOrderStatus = async () => {
    if (!openOrder?.id || savingStatus) return;
    setSavingStatus(true);
    try {
      const updated = await apiService.updateCatalogOrder(openOrder.id, { status: draftStatus });
      const next = updated?.data || { ...openOrder, status: draftStatus };
      showSuccess('Status atualizado.');
      setOpenOrder(next);
      setEditingStatus(false);
      loadOrders();
    } catch (error) {
      showWarning(error?.message || 'Falha ao atualizar status');
    } finally {
      setSavingStatus(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return value;
    }
  };

  const filteredHint = useMemo(() => {
    if (tab === 'products') return `${products.length} produto${products.length === 1 ? '' : 's'}`;
    return `${total} pedido${total === 1 ? '' : 's'}`;
  }, [tab, products.length, total]);

  return (
    <div className="page-container catalogo-page">
      {notifications.map((n) => (
        <Notification key={n.id} {...n} onClose={() => removeNotification(n.id)} />
      ))}

      <div className="breadcrumb">
        <span>Painel</span>
        <span>Catálogo</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Catálogo</h1>
          <p>
            Lance calcinhas e chinelos um a um, ou importe um PDF de chinelos. Na importação você
            escolhe se oculta os chinelos anteriores ou se soma outro PDF ao mesmo catálogo. Calcinhas
            e pedidos já feitos continuam no histórico.
          </p>
        </div>
        {tab === 'products' && (
          <div className="catalog-header-actions">
            <button
              className="btn-catalog-import"
              type="button"
              disabled={importingPdf}
              onClick={openImportModal}
            >
              <span className="btn-catalog-import-icon">
                <HiDocumentAdd size={20} />
              </span>
              <span className="btn-catalog-import-copy">
                <strong>{importingPdf ? 'Importando PDF...' : 'Importar PDF'}</strong>
                <small>Chinelos</small>
              </span>
            </button>
            <button className="btn-primary" type="button" onClick={openNew}>
              <HiPlus size={18} /> Novo produto
            </button>
          </div>
        )}
      </div>

      <div className="catalog-tabs">
        <button type="button" className={tab === 'products' ? 'is-active' : ''} onClick={() => { setTab('products'); setSearchTerm(''); }}>
          <HiColorSwatch size={18} /> Produtos
        </button>
        <button
          type="button"
          className={tab === 'orders' ? 'is-active' : ''}
          onClick={() => { setTab('orders'); setSearchTerm(''); setCurrentPage(1); }}
        >
          Pedidos
          {pendingCount > 0 && <span className="catalog-badge">{pendingCount}</span>}
        </button>
      </div>

      <div className="page-actions exclusao-toolbar">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder={tab === 'products' ? 'Buscar produto...' : 'Buscar por nome, telefone ou nº do pedido...'}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        {tab === 'products' ? (
          <>
          <select
            className="status-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Todas as categorias</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <select
            className="status-filter"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="true">Ativos (site)</option>
            <option value="false">Ocultos (anteriores)</option>
            <option value="">Todos</option>
          </select>
          </>
        ) : (
          <select
            className="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        )}
        <span className="catalog-count">{filteredHint}</span>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={tab === 'products' ? 7 : 6} />
        ) : tab === 'products' ? (
          products.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum produto neste filtro. Importe um PDF de chinelos ou clique em Novo produto.</p>
              <button className="btn-catalog-import" type="button" onClick={openImportModal}>
                <span className="btn-catalog-import-icon">
                  <HiDocumentAdd size={20} />
                </span>
                <span className="btn-catalog-import-copy">
                  <strong>Importar PDF</strong>
                  <small>Cadastra como chinelo</small>
                </span>
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Modelo</th>
                    <th>Categoria</th>
                    <th>Valor</th>
                    <th>Tamanhos</th>
                    <th>Cores</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="catalog-product-cell">
                          {item.image_url ? (
                            <img src={item.image_url} alt="" />
                          ) : (
                            <span className="catalog-thumb-fallback" />
                          )}
                          <div>
                            <strong>{item.name}</strong>
                            {item.description && <p>{item.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td>{item.model || '—'}</td>
                      <td>{item.category_label}</td>
                      <td>
                        {money(item.price)}
                        {item.category === 'chinelo' && (
                          <div className="muted">mín. 12 pares · taxa R$ 150 no pedido</div>
                        )}
                      </td>
                      <td>{(sizesForProduct(item).join(', ') || '—')}</td>
                      <td>{(item.colors || []).join(', ') || '—'}</td>
                      <td>
                        <span className={`status-badge ${item.active ? 'status-processed' : 'status-rejected'}`}>
                          {item.active ? 'Ativo' : 'Oculto'}
                        </span>
                        {item.source === 'pdf' && <div className="muted">PDF</div>}
                      </td>
                      <td>
                        <div className="actions">
                          <button className="btn-icon btn-edit" type="button" onClick={() => openEdit(item)} title="Editar">
                            <HiPencil size={18} />
                          </button>
                          <button className="btn-icon btn-delete" type="button" onClick={() => setProductToDelete(item)} title="Excluir">
                            <HiTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum pedido encontrado.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Itens</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const photos = uniqueOrderProducts(order, resolveItemImage);
                    const itemCount = (order.items || []).reduce((sum, i) => sum + Number(i.quantity || 0), 0);
                    return (
                    <tr key={order.id}>
                      <td>{order.id}</td>
                      <td>
                        <strong>{order.customer_name}</strong>
                        <div className="muted">{order.customer_phone}</div>
                      </td>
                      <td>
                        <div className="catalog-order-products">
                          <div className="catalog-order-thumbs">
                            {photos.thumbs.length === 0 ? (
                              <span className="catalog-thumb-fallback" />
                            ) : (
                              photos.thumbs.map((item) => (
                                <OrderItemPhoto
                                  key={item.id}
                                  src={item.preview}
                                  alt={item.product_name}
                                />
                              ))
                            )}
                            {photos.extra > 0 && <span className="catalog-thumb-more">+{photos.extra}</span>}
                          </div>
                          <div className="catalog-order-products-copy">
                            <strong>{itemCount} {itemCount === 1 ? 'item' : 'itens'}</strong>
                            <p>
                              {(order.items || []).slice(0, 2).map((i) => i.product_name).join(', ') || '—'}
                              {(order.items || []).length > 2 ? '…' : ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{money(order.total)}</td>
                      <td>
                        <span className={`status-badge status-${order.status}`}>{STATUS_LABELS[order.status] || order.status}</span>
                      </td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        <div className="actions">
                          <button className="catalog-open-btn" type="button" onClick={() => openOrderModal(order)}>
                            Abrir
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="table-footer">
              <span>Página {currentPage} de {lastPage}</span>
              <div className="pagination-controls">
                <button type="button" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</button>
                <button type="button" disabled={currentPage >= lastPage} onClick={() => setCurrentPage((p) => p + 1)}>›</button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content catalog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar produto' : 'Novo produto'}</h2>
              <button className="modal-close" type="button" onClick={() => setShowModal(false)}>
                <HiX size={20} />
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              <div className="catalog-form-grid">
                <div className="form-group">
                  <label>Categoria *</label>
                  <select value={form.category} onChange={(e) => onCategoryChange(e.target.value)} required>
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Valor do modelo (R$) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                    required
                  />
                  {form.category === 'chinelo' && form.price !== '' && (
                    <small className="catalog-hint-field">
                      No site: {money(Number(form.price))} · mínimo 12 pares. Taxa de Intermediação de R$ 150 no total do pedido.
                    </small>
                  )}
                </div>
              </div>
              {form.category === 'chinelo' && (
                <div className="form-group">
                  <label>Modelo *</label>
                  <input
                    value={form.model}
                    onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
                    placeholder="Ex: Slide tradicional"
                    required
                  />
                  <small className="catalog-hint-field">
                    Variações do mesmo modelo aparecem juntas no site. Se o valor for diferente, cada uma mantém o próprio preço.
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Nome *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder={form.category === 'chinelo' ? 'Ex: Slide tradicional preto' : 'Ex: Calcinha renda alta'}
                  required
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Tecido, caimento, observações..."
                />
              </div>
              <TagField
                label="Tamanhos"
                values={form.sizes}
                onChange={(sizes) =>
                  setForm((p) => ({
                    ...p,
                    sizes: p.category === 'chinelo' ? pairChineloSizes(sizes) : sizes,
                  }))
                }
                placeholder="Digite e Enter"
                suggestions={SIZE_PRESETS[form.category] || []}
              />
              <TagField
                label="Cores"
                values={form.colors}
                onChange={(colors) => setForm((p) => ({ ...p, colors }))}
                placeholder="Digite e Enter"
                suggestions={COLOR_PRESETS}
              />
              <div className="form-group">
                <label>Imagem {editingId ? '' : '*'}</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => handleImage(e.target.files?.[0])}
                />
                {imagePreview && <img className="catalog-preview" src={imagePreview} alt="Prévia" />}
              </div>
              <div className="catalog-form-grid">
                <div className="form-group">
                  <label>Ordem</label>
                  <input
                    type="number"
                    min="0"
                    value={form.sort_order}
                    onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                  />
                </div>
                <label className="catalog-switch">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                  />
                  Visível no catálogo do site
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : editingId ? 'Salvar' : 'Lançar produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openOrder && (
        <div className="modal-overlay" onClick={closeOrderModal}>
          <div className="modal-content catalog-modal catalog-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Pedido #{openOrder.id}</h2>
              <button className="modal-close" type="button" onClick={closeOrderModal}>
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="catalog-order-customer">
                <strong>{openOrder.customer_name}</strong>
                <p>{openOrder.customer_phone}{openOrder.customer_email ? ` · ${openOrder.customer_email}` : ''}</p>
                <p className="muted">{formatDate(openOrder.created_at)}</p>
                {openOrder.notes && <p className="catalog-order-notes">{openOrder.notes}</p>}
              </div>
              <h3 className="catalog-order-section">Itens escolhidos</h3>
              <ul className="catalog-order-items">
                {(openOrder.items || []).map((item) => (
                  <li key={item.id}>
                    <OrderItemPhoto src={resolveItemImage(item)} alt={item.product_name} />
                    <div className="catalog-order-item-copy">
                      <strong>{item.quantity}× {item.product_name}</strong>
                      <p>
                        {[categoryLabel(item.category), itemMeta(item)].filter(Boolean).join(' · ') || '—'}
                        {Number(item.price_multiplier) > 1 ? ` · total ×${Number(item.price_multiplier)}` : ''}
                      </p>
                      <span className="muted">{money(item.unit_price)} un.</span>
                    </div>
                    <strong className="catalog-order-item-sub">{money(item.subtotal)}</strong>
                  </li>
                ))}
              </ul>
              {Number(openOrder.intermediation_fee) > 0 && (
                <div className="catalog-order-breakdown">
                  <p>
                    <span>Subtotal</span>
                    <span>{money(Number(openOrder.total) - Number(openOrder.intermediation_fee))}</span>
                  </p>
                  <p>
                    <span>Taxa de Intermediação</span>
                    <span>{money(openOrder.intermediation_fee)}</span>
                  </p>
                </div>
              )}
              <p className="catalog-order-total">Total {money(openOrder.total)}</p>
              <div className="catalog-order-status-row">
                <div>
                  <label>Status</label>
                  {editingStatus ? (
                    <select
                      value={draftStatus}
                      disabled={savingStatus}
                      onChange={(e) => setDraftStatus(e.target.value)}
                    >
                      {Object.entries(STATUS_LABELS).map(([id, label]) => (
                        <option key={id} value={id}>{label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`status-badge status-${openOrder.status}`}>
                      {STATUS_LABELS[openOrder.status] || openOrder.status}
                    </span>
                  )}
                </div>
                {editingStatus ? (
                  <div className="catalog-order-status-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={savingStatus}
                      onClick={() => {
                        setDraftStatus(openOrder.status);
                        setEditingStatus(false);
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={savingStatus || draftStatus === openOrder.status}
                      onClick={saveOrderStatus}
                    >
                      {savingStatus ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                ) : (
                  <button type="button" className="catalog-edit-status-btn" onClick={() => setEditingStatus(true)}>
                    <HiPencil size={16} /> Editar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!productToDelete}
        type="danger"
        title="Excluir produto"
        message={productToDelete ? `Excluir "${productToDelete.name}" do catálogo? A foto também será removida. Pedidos já feitos continuam no histórico.` : ''}
        confirmText="Excluir"
        onCancel={() => setProductToDelete(null)}
        onConfirm={confirmDelete}
      />
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content catalog-modal catalog-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Importar PDF de chinelos</h2>
              <button className="modal-close" type="button" onClick={closeImportModal} disabled={importingPdf}>
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="catalog-import-badge">Categoria: Chinelo</div>
              <p className="catalog-import-lead">
                Os modelos extraídos entram no catálogo como <strong>chinelo</strong>, agrupados por
                modelo. No site o preço é o valor do modelo, com mínimo de 12 pares. No total do
                pedido entra a <strong>Taxa de Intermediação de R$ 150</strong>.
              </p>
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={onPdfPicked}
              />
              <button
                type="button"
                className={`catalog-dropzone${pdfDragOver ? ' is-over' : ''}${pendingPdf ? ' has-file' : ''}`}
                disabled={importingPdf}
                onClick={() => pdfInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setPdfDragOver(true);
                }}
                onDragLeave={() => setPdfDragOver(false)}
                onDrop={onPdfDrop}
              >
                <HiUpload size={28} />
                {pendingPdf ? (
                  <>
                    <strong>{pendingPdf.name}</strong>
                    <span>{(pendingPdf.size / 1024).toFixed(0)} KB · clique ou solte outro PDF para trocar</span>
                  </>
                ) : (
                  <>
                    <strong>Arraste o PDF aqui</strong>
                    <span>ou clique para selecionar o arquivo</span>
                  </>
                )}
              </button>
              <label className="catalog-switch catalog-import-check">
                <input
                  type="checkbox"
                  checked={deactivatePrevious}
                  onChange={(e) => setDeactivatePrevious(e.target.checked)}
                  disabled={importingPdf}
                />
                Ocultar chinelos anteriores
              </label>
              <p className="muted">
                {deactivatePrevious
                  ? 'Os chinelos ativos atuais ficam ocultos. As calcinhas continuam ativas. Pedidos já feitos seguem no histórico.'
                  : 'Os chinelos já cadastrados continuam ativos. Marque só no primeiro PDF; nos próximos do mesmo catálogo, deixe desmarcado.'}
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={closeImportModal} disabled={importingPdf}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmImportPdf}
                disabled={!pendingPdf || importingPdf}
              >
                {importingPdf ? 'Importando...' : 'Importar chinelos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogo;
